<script>
import Chart from 'chart.js/auto';
export default {
  props: {
    letalo: String,
    datumOd: String,
    datumDo: String,
    primerjavaLeto: Number
  },
  emits: ['odjava'],
  data() {
    return {
      nalaganje: false,
      podatki: null,
      primerjava: null,
      pilotiChart: null,
      kazalnikiChart: null,
      trendChart: null,
      chartInitTimeout: null,
      activeRequestToken: 0,
      isMounted: true,
      tableThemeStyle: {
        '--bs-table-bg': 'transparent',
        '--bs-table-color': '#ffffff',
        '--bs-table-border-color': 'rgba(255, 255, 255, 0.22)',
        '--bs-table-hover-color': '#ffffff',
        '--bs-table-hover-bg': 'rgba(255, 255, 255, 0.06)',
      }
    }
  },
  mounted() {
    this.pridobiPodatke();
  },
  beforeUnmount() {
    this.isMounted = false;
    this.activeRequestToken += 1;
    if (this.chartInitTimeout) {
      clearTimeout(this.chartInitTimeout);
      this.chartInitTimeout = null;
    }
    this.destroyCharts();
  },
  watch: {
    letalo() {
      this.pridobiPodatke();
    },
    datumOd() {
      this.pridobiPodatke();
    },
    datumDo() {
      this.pridobiPodatke();
    },
    primerjavaLeto() {
      this.pridobiPodatke();
    },
  },
  methods: {
    premakniDatumVPrimerjalnoLeto(datum, leto) {
      if (!datum || !leto) return null;
      const deli = datum.split('-');
      if (deli.length !== 3) return null;
      const mesec = parseInt(deli[1], 10);
      const dan = parseInt(deli[2], 10);
      if (!mesec || !dan) return null;
      const maxDan = new Date(leto, mesec, 0).getDate();
      const novDan = Math.min(dan, maxDan);
      return `${leto}-${deli[1]}-${String(novDan).padStart(2, '0')}`;
    },
    pridobiPodatke() {
      if (!this.datumOd || !this.datumDo) {
        this.podatki = null;
        this.primerjava = null;
        this.nalaganje = false;
        this.destroyCharts();
        return;
      }

      const requestToken = ++this.activeRequestToken;
      this.nalaganje = true;
      this.destroyCharts();

      // Clear any pending chart initialization
      if (this.chartInitTimeout) {
        clearTimeout(this.chartInitTimeout);
        this.chartInitTimeout = null;
      }

      const podatki = new URLSearchParams();
      podatki.append('letalo', this.letalo);
      podatki.append('datum_od', this.datumOd);
      podatki.append('datum_do', this.datumDo);

      axios.post('./api/porocila/vleke/', podatki, {withCredentials: true})
          .then((response) => {
            if (!this.isMounted || requestToken !== this.activeRequestToken) return;

            this.podatki = response.data.podatki;
            this.nalaganje = false;

            // Debounce chart initialization to prevent rapid recreation
            this.chartInitTimeout = setTimeout(() => {
              this.$nextTick(() => {
                if (!this.isMounted || requestToken !== this.activeRequestToken || !this.podatki) return;
                if (this.podatki) {
                  this.initCharts();
                }
              });
            }, 100);

            if (this.primerjavaLeto) {
              this.pridobiPrimerjavo(requestToken);
            } else {
              this.primerjava = null;
            }
          })
          .catch((napaka) => {
            if (!this.isMounted || requestToken !== this.activeRequestToken) return;
            if (napaka.response?.status === 401) {
              this.$router.push('/Logout');
            }
            this.podatki = null;
            this.destroyCharts();
            this.nalaganje = false;
          });
    },
    pridobiPrimerjavo(requestToken = this.activeRequestToken) {
      const primerjavaDatumOd = this.premakniDatumVPrimerjalnoLeto(this.datumOd, this.primerjavaLeto);
      const primerjavaDatumDo = this.premakniDatumVPrimerjalnoLeto(this.datumDo, this.primerjavaLeto);
      if (!primerjavaDatumOd || !primerjavaDatumDo) {
        this.primerjava = null;
        return;
      }

      const podatki = new URLSearchParams();
      podatki.append('letalo', this.letalo);
      podatki.append('datum_od', primerjavaDatumOd);
      podatki.append('datum_do', primerjavaDatumDo);

      axios.post('./api/porocila/vleke/', podatki, {withCredentials: true})
          .then((response) => {
            if (!this.isMounted || requestToken !== this.activeRequestToken) return;
            this.primerjava = response.data.podatki;
          })
          .catch(() => {
            if (!this.isMounted || requestToken !== this.activeRequestToken) return;
            this.primerjava = null;
          });
    },
    destroyCharts() {
      if (this.chartInitTimeout) {
        clearTimeout(this.chartInitTimeout);
        this.chartInitTimeout = null;
      }

      try {
        if (this.pilotiChart) {
          this.pilotiChart.stop();
          this.pilotiChart.destroy();
          this.pilotiChart = null;
        }
      } catch (e) {
        this.pilotiChart = null;
      }

      try {
        if (this.trendChart) {
          this.trendChart.stop();
          this.trendChart.destroy();
          this.trendChart = null;
        }
      } catch (e) {
        this.trendChart = null;
      }

      try {
        if (this.kazalnikiChart) {
          this.kazalnikiChart.stop();
          this.kazalnikiChart.destroy();
          this.kazalnikiChart = null;
        }
      } catch (e) {
        this.kazalnikiChart = null;
      }
    },
    parseTimeToMinutes(timeString) {
      if (!timeString) return 0;
      const [ure, minute] = String(timeString).split(':').map(Number);
      return ((ure || 0) * 60) + (minute || 0);
    },
    initCharts() {
      if (!Chart || !this.podatki || !this.isMounted || this.nalaganje) return;
      this.destroyCharts();

      const isCanvasValid = (canvas) => {
        if (!canvas || !canvas.getContext || !canvas.ownerDocument || !canvas.isConnected || !document.body.contains(canvas)) {
          return false;
        }
        const context2d = canvas.getContext('2d');
        return !!(context2d && typeof context2d.save === 'function' && typeof context2d.restore === 'function');
      };

      const createChartSafe = (canvas, config) => {
        if (!isCanvasValid(canvas)) return null;
        const context2d = canvas.getContext('2d');
        if (!context2d || typeof context2d.save !== 'function') return null;
        try {
          if (Chart.getChart) {
            const existingChart = Chart.getChart(canvas);
            if (existingChart) {
              try {
                existingChart.stop();
                existingChart.destroy();
              } catch (e) {
                // ignore stale instance cleanup issues
              }
            }
          }
          const chartConfig = {
            ...config,
            options: {
              animation: false,
              ...(config.options || {})
            }
          };
          return new Chart(context2d, chartConfig);
        } catch (e) {
          return null;
        }
      };

      const piloti = Array.isArray(this.podatki.piloti_vlacilcev) ? this.podatki.piloti_vlacilcev.slice(0, 8) : [];
      if (piloti.length > 0 && isCanvasValid(this.$refs.pilotiChartCanvas)) {
        this.pilotiChart = createChartSafe(this.$refs.pilotiChartCanvas, {
          type: 'bar',
          data: {
            labels: piloti.map((p) => p.naziv),
            datasets: [{
              label: 'Vlek (št.)',
              data: piloti.map((p) => Number(p.stevilo) || 0),
              backgroundColor: 'rgba(251, 191, 36, 0.8)',
              borderColor: 'rgba(251, 191, 36, 1)',
              borderWidth: 1,
              borderRadius: 6
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {legend: {display: false}},
            scales: {
              x: {ticks: {color: '#ffffff'}, grid: {color: 'rgba(255,255,255,0.12)'}},
              y: {ticks: {color: '#ffffff', precision: 0}, grid: {color: 'rgba(255,255,255,0.12)'}, beginAtZero: true}
            }
          }
        });
      }

      if (isCanvasValid(this.$refs.kazalnikiChartCanvas)) {
        this.kazalnikiChart = createChartSafe(this.$refs.kazalnikiChartCanvas, {
          type: 'bar',
          data: {
            labels: ['Vleke', 'Piloti', 'Jadralna letala'],
            datasets: [{
              label: 'Kazalniki',
              data: [
                Number(this.podatki.skupno_stevilo) || 0,
                Number(this.podatki.st_pilotov) || 0,
                Number(this.podatki.st_jadralnih) || 0,
              ],
              backgroundColor: ['rgba(251, 191, 36, 0.8)', 'rgba(96, 165, 250, 0.8)', 'rgba(52, 211, 153, 0.8)'],
              borderColor: ['rgba(251, 191, 36, 1)', 'rgba(96, 165, 250, 1)', 'rgba(52, 211, 153, 1)'],
              borderWidth: 1,
              borderRadius: 6
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {legend: {display: false}},
            scales: {
              x: {ticks: {color: '#ffffff'}, grid: {color: 'rgba(255,255,255,0.12)'}},
              y: {ticks: {color: '#ffffff', precision: 0}, grid: {color: 'rgba(255,255,255,0.12)'}, beginAtZero: true}
            }
          }
        });
      }

      const trend = Array.isArray(this.podatki.mesecni_trend) ? this.podatki.mesecni_trend : [];
      if (trend.length > 0 && isCanvasValid(this.$refs.trendChartCanvas)) {
        this.trendChart = createChartSafe(this.$refs.trendChartCanvas, {
          type: 'line',
          data: {
            labels: trend.map((m) => m.mesec_naziv || m.mesec),
            datasets: [{
              label: 'Vleke - minute',
              data: trend.map((m) => this.parseTimeToMinutes(m.cas)),
              borderColor: 'rgba(251, 191, 36, 1)',
              backgroundColor: 'rgba(251, 191, 36, 0.18)',
              fill: true,
              tension: 0.3
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {legend: {labels: {color: '#ffffff'}}},
            scales: {
              x: {ticks: {color: '#ffffff'}, grid: {color: 'rgba(255,255,255,0.12)'}},
              y: {ticks: {color: '#ffffff'}, grid: {color: 'rgba(255,255,255,0.12)'}, beginAtZero: true}
            }
          }
        });
      }
    },
    formatNumber(num) {
      return num ? num.toLocaleString('sl-SI') : '0';
    }
  }
}
</script>
<template>
  <div>
    <div v-if="nalaganje" class="text-center p-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Nalaganje...</span>
      </div>
    </div>

    <div v-else-if="podatki" class="row g-4">
      <!-- Skupne ure vleke -->
      <div class="col-md-3">
        <div class="card bg-cards text-white">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-white">
              <i class="fas fa-link"></i> Skupne ure vleke
            </h6>
            <h3 class="card-title mb-0">{{ podatki.skupno_cas }}</h3>
            <small class="text-white">{{ formatNumber(podatki.skupno_stevilo) }} vlek</small>
            <br>
            <small v-if="primerjava" class="text-white">vs. {{ primerjava.skupno_cas }} ({{ primerjavaLeto }})</small>
          </div>
        </div>
      </div>

      <!-- Povprečno -->
      <div class="col-md-3">
        <div class="card bg-cards text-white">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-white">
              <i class="fas fa-chart-line"></i> Povprečje ur/vleka
            </h6>
            <h3 class="card-title mb-0">{{ podatki.povprecje_cas }}</h3>
            <small class="text-white">{{ podatki.najdaljsa_vleka }} najdaljša</small>
            <br>
            <small v-if="primerjava" class="text-white">vs. {{ primerjava.povprecje_cas }} ({{ primerjavaLeto }})</small>
          </div>
        </div>
      </div>

      <!-- Različni piloti -->
      <div class="col-md-3">
        <div class="card bg-cards text-white">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-white">
              <i class="fas fa-users"></i> Različni piloti
            </h6>
            <h3 class="card-title mb-0">{{ formatNumber(podatki.st_pilotov) }}</h3>
            <small class="text-white">Najbolj aktiven: {{ podatki.najbolj_aktiven }}</small>
            <br>
            <small v-if="primerjava" class="text-white">vs. {{ formatNumber(primerjava.st_pilotov) }} ({{ primerjavaLeto }})</small>
          </div>
        </div>
      </div>

      <!-- Povlečena jadralna letala -->
      <div class="col-md-3">
        <div class="card bg-cards text-white">
          <div class="card-body">
            <h6 class="card-subtitle mb-2 text-white">
              <i class="fas fa-paper-plane"></i> Povlečena letala
            </h6>
            <h3 class="card-title mb-0">{{ formatNumber(podatki.st_jadralnih) }}</h3>
            <small class="text-white">Različnih letal</small>
            <br>
            <small v-if="primerjava" class="text-white">vs. {{ formatNumber(primerjava.st_jadralnih) }} ({{ primerjavaLeto }})</small>
          </div>
        </div>
      </div>

      <!-- Piloti vlačilcev - graf -->
      <div class="col-md-6">
        <div class="card bg-cards text-white border-0 shadow-sm rounded-4 h-100">
          <div class="card-header border-0 d-flex align-items-center gap-2">
            <i class="fas fa-chart-bar"></i>
            <h5 class="mb-0">TOP piloti vlačilcev - graf</h5>
          </div>
          <div class="card-body" style="min-height: 260px;">
            <canvas ref="pilotiChartCanvas"></canvas>
          </div>
        </div>
      </div>

      <!-- Ključni kazalniki - graf -->
      <div class="col-md-6">
        <div class="card bg-cards text-white border-0 shadow-sm rounded-4 h-100">
          <div class="card-header border-0 d-flex align-items-center gap-2">
            <i class="fas fa-chart-bar"></i>
            <h5 class="mb-0">Ključni kazalniki</h5>
          </div>
          <div class="card-body" style="min-height: 260px;">
            <canvas ref="kazalnikiChartCanvas"></canvas>
          </div>
        </div>
      </div>

      <!-- Mesečni trend vlek - graf -->
      <div class="col-12" v-if="podatki.mesecni_trend && podatki.mesecni_trend.length">
        <div class="card bg-cards text-white border-0 shadow-sm rounded-4 h-100">
          <div class="card-header border-0 d-flex align-items-center gap-2">
            <i class="fas fa-chart-line"></i>
            <h5 class="mb-0">Trend vlek - graf</h5>
          </div>
          <div class="card-body" style="min-height: 260px;">
            <canvas ref="trendChartCanvas"></canvas>
          </div>
        </div>
      </div>

      <!-- Piloti vlačilcev -->
      <div class="col-lg-6">
        <div class="card bg-cards text-white border-0 shadow-sm rounded-4">
          <div class="card-header border-0">
            <h5 class="mb-0">TOP piloti vlačilcev</h5>
          </div>
          <div class="card-body">
            <table class="table table-sm mb-0 align-middle" :style="tableThemeStyle">
              <thead>
              <tr>
                <th>#</th>
                <th>Pilot</th>
                <th class="text-end">Ure</th>
                <th class="text-end">Vlek</th>
                <th class="text-end">Povprečje</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(pilot, index) in podatki.piloti_vlacilcev" :key="pilot.sifra">
                <td>{{ index + 1 }}</td>
                <td>{{ pilot.naziv }}</td>
                <td class="text-end">{{ pilot.cas }}</td>
                <td class="text-end">{{ formatNumber(pilot.stevilo) }}</td>
                <td class="text-end">{{ pilot.povprecje }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Povlečena jadralna letala -->
      <div class="col-lg-6">
        <div class="card bg-cards text-white">
          <div class="card-header">
            <h5 class="mb-0">Povlečena letala</h5>
          </div>
          <div class="card-body">
            <table class="table table-sm mb-0 align-middle" :style="tableThemeStyle">
              <thead>
              <tr>
                <th>Registracija</th>
                <th>Tip</th>
                <th class="text-end">Vlek</th>
                <th class="text-end">%</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="letalo in podatki.jadralna_letala" :key="letalo.registracija">
                <td>{{ letalo.registracija }}</td>
                <td>{{ letalo.tip }}</td>
                <td class="text-end">{{ formatNumber(letalo.stevilo) }}</td>
                <td class="text-end">{{ letalo.odstotek }}%</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Mesečni trend -->
      <div class="col-12" v-if="podatki.mesecni_trend && podatki.mesecni_trend.length">
        <div class="card bg-cards text-white">
          <div class="card-header">
            <h5 class="mb-0">Mesečni trend vlek ({{ datumOd }} - {{ datumDo }})</h5>
          </div>
          <div class="card-body">
            <div class="table-responsive">
              <table class="table table-sm mb-0 align-middle" :style="tableThemeStyle">
                <thead>
                <tr>
                  <th>Mesec</th>
                  <th class="text-end">Ure</th>
                  <th class="text-end">Vlek</th>
                  <th class="text-end">Povprečje</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="mesec in podatki.mesecni_trend" :key="mesec.mesec">
                  <td>{{ mesec.mesec_naziv }}</td>
                  <td class="text-end">{{ mesec.cas }}</td>
                  <td class="text-end">{{ formatNumber(mesec.stevilo) }}</td>
                  <td class="text-end">{{ mesec.povprecje }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="alert alert-warning">
      <i class="fas fa-exclamation-triangle"></i> Ni podatkov za izbrano letalo in obdobje.
    </div>
  </div>
</template>